import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TouchableWithoutFeedback,
  Animated,
  Image,
  Easing,
  TextInput,
  Keyboard,
  ActivityIndicator,
} from "react-native";
import { AppTheme } from "../utils/theme";
import CustomSwitch from "./global/CustomSwitch";
import LinearGradient from "react-native-linear-gradient";
import FastImage from "react-native-fast-image";
import { AppIcons } from "../assets/images";
import DeviceInfo from "react-native-device-info";
import { showToastNotification } from "./global/Toast";
import { userAction } from "../redux/reducers/user";

interface SettingsModalProps {
  visible: boolean;
  deleteLoading: boolean;
  onClose: () => void;
  showlogout: boolean;
  music: boolean;
  onMusicChange: (value: boolean) => void;
  sound: boolean;
  onSoundChange: (value: boolean) => void;
  notifications: boolean;
  onNotificationsChange: (value: boolean) => void;
  onPrivacyPress: () => void;
  onTermsPress: () => void;
  onLogout?: () => void;
  onSignIn?: () => void;
  viewType: "settings" | "otp";
  onOTPSubmit?: (otp: string) => void; // New prop for OTP value callback (OTP view only)
  onIsDeleted: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  visible,
  deleteLoading,
  onClose,
  showlogout,
  music,
  onMusicChange,
  sound,
  onSoundChange,
  notifications,
  onNotificationsChange,
  onPrivacyPress,
  onTermsPress,
  onLogout,
  onSignIn,
  viewType,
  onOTPSubmit,
  onIsDeleted,
}) => {
  // Animations
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.6)).current;
  const rotateAnim = useRef(new Animated.Value(-90)).current;
  const buildVersion = DeviceInfo.getBuildNumber();
  const version = DeviceInfo.getVersion();
  console.log(
    "@ build number for app : ",
    buildVersion,
    ", version : ",
    version
  );

  // OTP state and refs (only used in OTP view)
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const inputRefs = useRef<(TextInput | null)[]>([]);

  const setInputRef = useCallback(
    (index: number) => (ref: TextInput | null) => {
      inputRefs.current[index] = ref;
    },
    []
  );

  const handleOtpChange = (text: string, index: number) => {
    if (/[^0-9]/.test(text)) return; // Only allow numbers
    if (text.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = text;
    setOtp(newOtp);

    // Auto-focus next input
    if (text && index < 3) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    } else if (!text && index > 0) {
      // Focus previous on backspace (basic handling)
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    } else if (text && index === 3) {
      // All digits entered, dismiss keyboard
      Keyboard.dismiss();
      setActiveIndex(-1);
    }
  };

  const handleFocus = (index: number) => {
    setActiveIndex(index);
  };

  const handleBlur = () => {
    setActiveIndex(-1);
  };

  const handleKeyPress = ({ nativeEvent: { key } }: any, index: number) => {
    if (key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    }
  };

  const handleDeleteAccount = () => {
    const fullOtp = otp.join("");
    if (fullOtp.length === 4) {
      onOTPSubmit?.(fullOtp); // Pass full OTP to parent
      console.log("Full OTP submitted:", fullOtp);
    } else {
      showToastNotification("", "Enter the 4-digit OTP.");
                userAction.setTouchSound(true);
      
      // TODO: Show error message (e.g., via alert or state)
    }
  };

  const handleOverlayPress = () => {
    Keyboard.dismiss();
  };

  useEffect(() => {
    if (visible) {
      // Reset animation values every time modal opens
      fadeAnim.setValue(0);
      scaleAnim.setValue(0.6);
      rotateAnim.setValue(-90);

      // Reset OTP if opening OTP view
      if (viewType === "otp") {
        setOtp(["", "", "", ""]);
        setActiveIndex(-1);
      }

      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 6,
          tension: 90,
          useNativeDriver: true,
        }),
        Animated.timing(rotateAnim, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.6,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(rotateAnim, {
          toValue: -90,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start(() => {
        onClose(); // CLOSE AFTER ANIMATION
      });
    }
  }, [visible, viewType]);

  // Helper function to render content based on viewType
  const renderContent = () => {
    if (viewType === "settings") {
      // Original settings view
      return (
        <>
          {/* MUSIC */}
          <View style={styles.row}>
            <View style={styles.row1}>
              <Image source={AppIcons.Music} style={styles.icons} />
              <Text style={styles.rowLabel}>Music</Text>
            </View>
            <CustomSwitch value={music} onChange={onMusicChange} />
          </View>

          <View style={styles.line1} />

          {/* SOUND */}
          <View style={styles.row}>
            <View style={styles.row1}>
              <Image source={AppIcons.Volume} style={styles.icons} />
              <Text style={styles.rowLabel}>Sounds</Text>
            </View>
            <CustomSwitch value={sound} onChange={onSoundChange} />
          </View>

          <View style={styles.line1} />

          {/* NOTIFICATIONS */}
          <View style={styles.row}>
            <View style={styles.row1}>
              <Image source={AppIcons.Notification} style={styles.icons} />
              <Text style={styles.rowLabel}>Notifications</Text>
            </View>
            <CustomSwitch
              value={notifications}
              onChange={onNotificationsChange}
            />
          </View>

          <View style={styles.line1} />

          {/* PRIVACY */}
          <View style={styles.row}>
            <View style={styles.row1}>
              <Image source={AppIcons.Privacy} style={styles.icons} />
              <Text style={styles.rowLabel}>Privacy Policy</Text>
            </View>
            <TouchableOpacity style={styles.viewBtn} onPress={onPrivacyPress}>
              <Text style={styles.viewText}>View</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.line1} />

          {/* TERMS */}
          <View style={styles.row}>
            <View style={styles.row1}>
              <Image source={AppIcons.Terms} style={styles.icons} />
              <Text style={styles.rowLabel}>Terms & Condition</Text>
            </View>
            <TouchableOpacity style={styles.viewBtn} onPress={onTermsPress}>
              <Text style={styles.viewText}>View</Text>
            </TouchableOpacity>
          </View>
          {showlogout && onLogout && (
            <>
              <View style={styles.line1} />
              <View style={styles.row}>
                <TouchableOpacity
                  disabled={deleteLoading}
                  onPress={onIsDeleted}
                  style={styles.row1}
                >
                  <Image source={AppIcons.Delete} style={styles.icons} />
                  <Text style={styles.rowLabel1}>Delete Account</Text>
                </TouchableOpacity>
                {deleteLoading && (
                  <View style={styles.viewBtn1}>
                    <ActivityIndicator
                      size={"small"}
                      color={AppTheme.colors.white}
                    />
                  </View>
                )}
              </View>
            </>
          )}
          <View style={styles.line1} />

          {/* LOGOUT BUTTON */}
          <TouchableOpacity
            style={styles.logoutContainer}
            onPress={showlogout && onLogout ? onLogout : onSignIn}
          >
            <LinearGradient
              colors={[
                AppTheme.colors.linearGradient_9,
                AppTheme.colors.linearGradient_10,
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.appleButtonOuter}
            >
              <LinearGradient
                colors={[
                  AppTheme.colors.linearGradient_7,
                  AppTheme.colors.linearGradient_8,
                ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.appleButtonInner}
              >
                <Text style={styles.appleButtonText}>
                  {showlogout ? "Logout" : "Sign in"}
                </Text>
              </LinearGradient>
            </LinearGradient>
          </TouchableOpacity>
        </>
      );
    } else if (viewType === "otp") {
      // OTP view with 4 input boxes
      return (
        <>
          <Text style={styles.headingOtp}>
            {
              "To delete Account, enter the 4-digit OTP sent to your registered account"
            }
          </Text>
          <View style={styles.line1} />
          <View style={styles.otpContainer}>
            {otp.map((digit, index) => (
              <TextInput
                key={index}
                ref={setInputRef(index)}
                style={[
                  styles.otpInput,
                  {
                    borderColor:
                      index === activeIndex || digit
                        ? AppTheme.colors.white
                        : AppTheme.colors.white,
                    borderWidth: index === activeIndex ? 3 : 2,
                  },
                ]}
                value={digit}
                onChangeText={(text) => handleOtpChange(text, index)}
                onFocus={() => handleFocus(index)}
                onBlur={handleBlur}
                onKeyPress={(e) => handleKeyPress(e, index)}
                maxLength={1}
                keyboardType="number-pad"
                textAlign="center"
                autoCapitalize="none"
                selectTextOnFocus
              />
            ))}
          </View>
          <View style={styles.line1} />
          <TouchableOpacity
            style={styles.logoutContainer}
            onPress={handleDeleteAccount}
            disabled={deleteLoading}
          >
            <LinearGradient
              colors={[
                AppTheme.colors.linearGradient_9,
                AppTheme.colors.linearGradient_10,
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.appleButtonOuter}
            >
              <LinearGradient
                colors={[
                  AppTheme.colors.linearGradient_7,
                  AppTheme.colors.linearGradient_8,
                ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.appleButtonInner}
              >
                {deleteLoading ? (
                  <ActivityIndicator
                    size={"small"}
                    color={AppTheme.colors.white}
                  />
                ) : (
                  <Text style={styles.appleButtonText}>{"Delete Account"}</Text>
                )}
              </LinearGradient>
            </LinearGradient>
          </TouchableOpacity>
        </>
      );
    }

    return null; // Fallback
  };

  return (
    <Modal visible={visible} transparent animationType="none">
      <TouchableWithoutFeedback onPress={handleOverlayPress} accessible={false}>
        <Animated.View style={[styles.overlay, { opacity: fadeAnim }]}>
          <TouchableWithoutFeedback onPress={() => {}} accessible={false}>
            <Animated.View
              style={[
                styles.card,
                {
                  transform: [
                    { perspective: 1000 }, // REQUIRED for 3D
                    {
                      rotateX: rotateAnim.interpolate({
                        inputRange: [0, 90],
                        outputRange: ["0deg", "90deg"],
                      }),
                    },
                    { scale: scaleAnim },
                  ],
                },
              ]}
            >
              {/* CLOSE BUTTON */}
              <TouchableOpacity
                style={styles.crossBtnInside}
                onPress={() => onClose()}
              >
                <FastImage
                  source={AppIcons.Cross}
                  resizeMode={FastImage.resizeMode.contain}
                  style={styles.crossIcon}
                />
              </TouchableOpacity>

              {/* HEADER - Dynamic based on viewType */}
              <View style={styles.header}>
                <Text style={styles.heading}>
                  {viewType === "settings" ? "SETTINGS" : "ENTER OTP"}
                </Text>
              </View>

              <View style={styles.line} />

              {/* Dynamic Content */}
              {renderContent()}

              {/* Version Text (only for settings view, optional) */}
              {viewType === "settings" && (
                <Text style={styles.versionText}>
                  Version : {`${buildVersion} (${version})`}
                </Text>
              )}
            </Animated.View>
          </TouchableWithoutFeedback>
        </Animated.View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default SettingsModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },

  card: {
    width: "90%",
    backgroundColor: AppTheme.colors.settingModal,
    borderRadius: 70,
    borderWidth: 6,
    borderColor: AppTheme.colors.border,
    paddingBottom: 20,
    boxShadow: [
      {
        offsetX: 0,
        offsetY: 0,
        blurRadius: "25px",
        spreadDistance: "1px",
        color: `${AppTheme.colors.black}30`,
        inset: true,
      },
    ],
  },
  crossBtnInside: {
    position: "absolute",
    top: -10,
    right: -7,
    zIndex: 50,
  },
  crossIcon: {
    width: 55,
    height: 55,
  },

  header: {
    backgroundColor: AppTheme.colors.settingTextBack,
    paddingVertical: 10,
    marginHorizontal: "25%",
    borderBottomRightRadius: 20,
    borderBottomLeftRadius: 20,
    alignItems: "center",
  },

  heading: {
    fontSize: 32,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    color: AppTheme.colors.white,
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
  },
  headingOtp: {
    fontSize: 26,
    marginHorizontal: 30,
    marginVertical: 20,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    color: AppTheme.colors.white,
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
    textAlign: "center",
  },
  line: {
    marginTop: 30,
    borderTopWidth: 0.5,
    marginHorizontal: 20,
    borderTopColor: "rgba(0,0,0,0.1)",
  },

  line1: {
    borderTopWidth: 0.5,
    marginHorizontal: 20,
    borderTopColor: "rgba(0,0,0,0.1)",
  },

  // OTP-specific styles
  otpContainer: {
    flexDirection: "row",
    justifyContent: "space-evenly",
    marginHorizontal: 20,
    marginVertical: 20,
  },
  otpInput: {
    width: 60,
    height: 60,
    borderRadius: 10,
    fontSize: 24,
    color: AppTheme.colors.white,
    backgroundColor: "rgba(255,255,255,0.1)",
    textAlign: "center",
    fontFamily: AppTheme.fonts.ALEXANDRIA_REGULAR,
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 16,
  },

  row1: {
    flexDirection: "row",
    alignItems: "center",
  },

  icons: {
    height: 20,
    width: 20,
  },

  rowLabel: {
    fontSize: 18,
    fontFamily: AppTheme.fonts.ALEXANDRIA_REGULAR,
    color: AppTheme.colors.white,
    marginLeft: 10,
  },
  rowLabel1: {
    fontSize: 18,
    fontFamily: AppTheme.fonts.ALEXANDRIA_REGULAR,
    color: AppTheme.colors.error,
    marginLeft: 10,
  },
  viewBtn: {
    backgroundColor: "rgba(255,255,255,0.5)",
    borderColor: AppTheme.colors.white,
    borderWidth: 1,
    paddingHorizontal: 15,
    paddingVertical: 4,
    borderRadius: 20,
  },
  viewBtn1: {
    paddingHorizontal: 15,
    paddingVertical: 4,
    borderRadius: 20,
  },

  viewText: {
    color: AppTheme.colors.brown,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 20,
  },

  logoutContainer: {
    alignItems: "center",
    marginTop: 20,
  },

  appleButtonOuter: {
    width: 200,
    height: 55,
    borderRadius: 30,
  },

  appleButtonInner: {
    borderRadius: 30,
    height: 50,
    justifyContent: "center",
    alignItems: "center",
  },

  appleButtonText: {
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 22,
    color: AppTheme.colors.white,
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
    textAlign: "center",
  },
  versionText: {
    alignSelf: "center",
    marginTop: 10,
    fontFamily: AppTheme.fonts.ALEXANDRIA_MEDIUM,
    color: AppTheme.colors.white,
  },
});
